// AddChannelDialog · Lot M4
// Ajout d'un canal — slug libre kebab-case + autocomplete depuis catalogue.

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter }
  from "@/components/ui/dialog";

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,48}[a-z0-9])?$/;

const inputCls =
  "h-11 rounded-lg bg-white px-3 text-[13.5px] outline-none " +
  "focus:ring-2 focus:ring-[#C8FF2F]/40";
const inputStyle = { boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" };

export default function AddChannelDialog({
  open, onOpenChange, catalog = [], existingSlugs = [], onSubmit,
}) {
  const [slug, setSlug] = useState("");
  const [label, setLabel] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [externalId, setExternalId] = useState("");
  const [status, setStatus] = useState("active");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSlug(""); setLabel(""); setExternalUrl(""); setExternalId("");
      setStatus("active");
    }
  }, [open]);

  const suggestions = (catalog || []).filter(c => !existingSlugs.includes(c.slug));
  const slugValid = SLUG_RE.test(slug);
  const slugTaken = existingSlugs.includes(slug);

  const pickFromCatalog = (c) => {
    setSlug(c.slug);
    setLabel(c.label || "");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!slug.trim()) return toast.error("Slug requis");
    if (!slugValid) return toast.error("Slug invalide (kebab-case lowercase)");
    if (slugTaken) return toast.error("Ce canal existe déjà pour ce mandat");
    setSubmitting(true);
    try {
      await onSubmit?.({
        channel: slug.trim(),
        label: label.trim() || null,
        status,
        external_url: externalUrl.trim() || null,
        external_listing_id: externalId.trim() || null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px]" data-testid="add-channel-dialog">
        <DialogHeader>
          <DialogTitle className="text-[19px] font-semibold tracking-[-0.01em]">
            Ajouter un canal de publication
          </DialogTitle>
          <p className="text-[12.5px] text-black/50 max-w-[420px]">
            Choisissez un canal du catalogue ou saisissez un slug libre.
            Le net vendeur n&apos;est jamais transmis en publication.
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="mt-3 space-y-4">
          {/* Catalogue */}
          {suggestions.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-black/55 mb-2">
                Canaux suggérés
              </div>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((c) => (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => pickFromCatalog(c)}
                    className={`rounded-full px-3 py-1.5 text-[12px] transition-colors ${
                      slug === c.slug
                        ? "bg-black text-white"
                        : "bg-white text-black/70 hover:text-black"
                    }`}
                    style={slug !== c.slug ? { boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" } : {}}
                    data-testid={`catalog-pick-${c.slug}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] uppercase tracking-[0.14em] text-black/55">
                Slug canal <span className="text-[#EF4444]">*</span>
              </span>
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="leboncoin"
                className={inputCls} style={inputStyle}
                data-testid="channel-slug-input"
              />
              {slug && !slugValid && (
                <span className="text-[11px] text-[#B91C1C]">
                  Lowercase kebab-case uniquement (a-z 0-9 -)
                </span>
              )}
              {slugTaken && (
                <span className="text-[11px] text-[#B91C1C]">
                  Ce canal existe déjà.
                </span>
              )}
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] uppercase tracking-[0.14em] text-black/55">
                Label (facultatif)
              </span>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Leboncoin"
                className={inputCls} style={inputStyle}
                data-testid="channel-label-input"
              />
            </label>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.14em] text-black/55">
              URL de l&apos;annonce (facultatif)
            </span>
            <input
              type="url"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="https://…"
              className={inputCls} style={inputStyle}
              data-testid="channel-url-input"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.14em] text-black/55">
              ID annonce externe (facultatif)
            </span>
            <input
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              placeholder="LBC-12345…"
              className={inputCls} style={inputStyle}
              data-testid="channel-external-id-input"
            />
          </label>

          <div>
            <span className="text-[11px] uppercase tracking-[0.14em] text-black/55">
              Statut initial
            </span>
            <div className="mt-2 flex gap-2">
              {[
                { key: "active", label: "Actif (mis en ligne)" },
                { key: "draft",  label: "Brouillon" },
              ].map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setStatus(s.key)}
                  className={`rounded-full px-3 py-1.5 text-[12.5px] transition-colors ${
                    status === s.key
                      ? "bg-black text-white"
                      : "bg-white text-black/70 hover:text-black"
                  }`}
                  style={status !== s.key ? { boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" } : {}}
                  data-testid={`channel-status-${s.key}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <DialogFooter className="mt-2 gap-2 sm:justify-end">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-full bg-white px-4 py-2 text-[13px] font-medium text-black/70 hover:text-black"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
              data-testid="add-channel-cancel-btn"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || !slugValid || slugTaken}
              className="rounded-full bg-[#C8FF2F] px-5 py-2 text-[13px] font-medium text-black transition-transform hover:scale-[1.005] disabled:opacity-40 disabled:hover:scale-100"
              data-testid="add-channel-submit-btn"
            >
              {submitting ? "Ajout…" : (status === "active" ? "Publier" : "Ajouter en brouillon")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
